import { type FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Input, Modal, type Column } from "@legal-500/legal500-design-system";
import { powerlistSeries, type Series } from "../../api/powerlist.service";
import { DataTable } from "../../components/DataTable/DataTable";
import EmptyState from "../../components/EmptyState";
import { ErrorBanner } from "../../components/ErrorBanner/ErrorBanner";
import LoadingState from "../../components/LoadingState";
import { PageHeader } from "../../components/PageHeader";
import { useQuery } from "../../hooks/useApi";
import { ROUTES } from "../../routes";
import { ConfirmModal, FormError, errorMessage, usePowerlistAccess, useToast } from "./shared";
import styles from "./Powerlist.module.css";

type SeriesFormModalProps = {
  isOpen: boolean;
  series: Series | null;
  onClose: () => void;
  onSaved: (series: Series) => void;
};

function SeriesFormModal({ isOpen, series, onClose, onSaved }: SeriesFormModalProps) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(series?.name ?? "");
    setSlug(series?.slug ?? "");
    setError(null);
    setSaving(false);
  }, [isOpen, series]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    setSaving(true);
    setError(null);
    const input = { name: name.trim(), slug: slug.trim() || undefined };
    try {
      onSaved(
        series
          ? await powerlistSeries.update(series.id, input)
          : await powerlistSeries.create(input),
      );
    } catch (saveError) {
      setError(errorMessage(saveError));
      setSaving(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={series ? "Edit series" : "New series"}>
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <FormError message={error} />
        <Input
          id="powerlist-series-name"
          label="Name"
          value={name}
          onChange={event => setName(event.target.value)}
          required
          fullWidth
          autoFocus
        />
        <Input
          id="powerlist-series-slug"
          label="Slug"
          value={slug}
          onChange={event => setSlug(event.target.value)}
          helperText={series ? undefined : "Leave blank to generate it from the name"}
          fullWidth
        />
        <div className={styles.formActions}>
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={saving}>
            {saving ? "Saving..." : series ? "Save" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

type FormState = { open: false } | { open: true; series: Series | null };

export default function SeriesListPage() {
  const { canWrite, canDelete } = usePowerlistAccess();
  const { data, loading, error, refetch, isRefetching } = useQuery(
    () => powerlistSeries.list(),
    [],
  );
  const [form, setForm] = useState<FormState>({ open: false });
  const [deleting, setDeleting] = useState<Series | null>(null);
  const { toast, success } = useToast();
  const series = [...(data ?? [])].sort((a, b) => a.name.localeCompare(b.name));

  const columns: Column<Series>[] = [
    {
      header: "Name",
      accessor: "name",
      render: (_value: unknown, row: Series) => (
        <Link to={ROUTES.powerlist.seriesDetail(row.id)}>{row.name}</Link>
      ),
    },
    { header: "Slug", accessor: "slug" },
    ...(canWrite || canDelete
      ? [
          {
            header: "Actions",
            accessor: "id" as const,
            render: (_value: unknown, row: Series) => (
              <div className={styles.actions}>
                {canWrite && (
                  <Button
                    variant="outlined"
                    size="small"
                    aria-label={`Edit ${row.name}`}
                    onClick={() => setForm({ open: true, series: row })}
                  >
                    Edit
                  </Button>
                )}
                {canDelete && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    aria-label={`Delete ${row.name}`}
                    onClick={() => setDeleting(row)}
                  >
                    Delete
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className={styles.wrapper}>
      <PageHeader title="GC Powerlist" subtitle="Series and their yearly editions">
        <div className={styles.headerActions}>
          <Link to={ROUTES.powerlist.questions}>Question bank</Link>
          {canWrite && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setForm({ open: true, series: null })}
            >
              New series
            </Button>
          )}
        </div>
      </PageHeader>

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}

      {loading && data === null ? (
        <LoadingState message="Loading series..." />
      ) : !error && series.length === 0 ? (
        <EmptyState variant="empty" title="No series" message="No series have been created yet." />
      ) : (
        !error && (
          <div className={styles.tableContainer}>
            <DataTable data={series} columns={columns} rowKey="id" emptyMessage="No series." />
          </div>
        )
      )}

      <SeriesFormModal
        isOpen={form.open}
        series={form.open ? form.series : null}
        onClose={() => setForm({ open: false })}
        onSaved={saved => {
          setForm({ open: false });
          success(`${saved.name} saved`);
          void refetch();
        }}
      />

      <ConfirmModal
        isOpen={deleting !== null}
        title="Delete series"
        message={`Delete ${deleting?.name ?? "this series"} with all its editions, lists and entries? This cannot be undone.`}
        confirmLabel="Delete"
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await powerlistSeries.remove(deleting.id);
          success(`${deleting.name} deleted`);
          setDeleting(null);
          await refetch();
        }}
      />

      {toast}
    </div>
  );
}
